// Offres Sauvegardées - Navigation interne au Cockpit
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Bookmark, Building2, MapPin, Euro, Clock, Trash2, Send, Eye, Bell, Search } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { useCurrentUser } from '../../../hooks/useCurrentUser';

function OffreCard({ offre, onRemove, onPostuler }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-gray-200 p-4"
    >
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
          {offre.logo ? (
            <img src={offre.logo} alt="" className="w-full h-full object-contain rounded-xl" />
          ) : (
            <Building2 size={24} className="text-gray-400" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900">{offre.title}</h3>
          <p className="text-xs text-cyan-600 font-medium">{offre.company}</p>
          
          <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <MapPin size={12} />
              {offre.location}
            </span>
            <span className="flex items-center gap-1">
              <Euro size={12} />
              {offre.salary}
            </span>
            <span className="flex items-center gap-1">
              <Clock size={12} />
              {offre.type}
            </span>
          </div>

          <p className="mt-2 text-xs text-gray-600 line-clamp-2">{offre.description}</p>

          <div className="flex flex-wrap gap-2 mt-2">
            {offre.tags?.map((tag, i) => (
              <span key={i} className="px-2 py-0.5 bg-cyan-50 text-cyan-700 text-xs font-medium rounded-full">
                {tag}
              </span>
            ))}
          </div>
        </div>
        <button
          onClick={() => onRemove(offre.id)}
          className="p-2 hover:bg-red-50 text-gray-400 hover:text-red-500 rounded-lg transition-colors"
        >
          <Trash2 size={16} />
        </button>
      </div>

      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
        <span className="text-xs text-gray-500">Sauvegardé {offre.savedDate}</span>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="text-xs h-8">
            <Eye size={12} className="mr-1" /> Voir
          </Button>
          <Button size="sm" className="bg-cyan-500 hover:bg-cyan-600 text-white text-xs h-8" onClick={() => onPostuler(offre)}>
            <Send size={12} className="mr-1" /> Postuler
          </Button>
        </div>
      </div>
    </motion.div>
  );
}

export default function OffresSauvegardees({ onNavigate }) {
  const { user } = useCurrentUser();
  
  const DEMO_OFFRES = [];

  const [offres, setOffres] = useState([]);

  useEffect(() => {
    if (user?.isDemo) {
      setOffres(DEMO_OFFRES);
    } else {
      setOffres([]);
    }
  }, [user]);

  const handleRemove = (id) => {
    setOffres(prev => prev.filter(o => o.id !== id));
  };

  const handlePostuler = (offre) => {
    console.log('Postuler:', offre.title);
  };

  // Navigation interne au cockpit
  const navigateTo = (context, label) => {
    if (onNavigate) {
      onNavigate(context, label);
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[16px] sm:text-xl font-bold text-[#1e3a5f]">Offres Sauvegardées</h1>
          <p className="text-sm text-gray-500">{offres.length} offre{offres.length > 1 ? 's' : ''}</p>
        </div>
        <Button 
          className="bg-cyan-500 hover:bg-cyan-600 text-white"
          onClick={() => navigateTo('page_emploi', 'Emploi')}
        >
          Rechercher des offres
        </Button>
      </div>

      {offres.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Bookmark size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Aucune offre sauvegardée</h3>
          <p className="text-xs text-gray-500 mb-4">Sauvegarde des offres pour les retrouver facilement</p>
          <Button 
            className="bg-cyan-500 hover:bg-cyan-600 text-white"
            onClick={() => navigateTo('page_emploi', 'Emploi')}
          >
            Parcourir les offres
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {offres.map(offre => (
            <OffreCard
              key={offre.id}
              offre={offre}
              onRemove={handleRemove}
              onPostuler={handlePostuler}
            />
          ))}
        </div>
      )}
    </div>
  );
}
